import { createTextLayer } from "../text/textLayer";
import { createRenderScheduler } from "../renderScheduler";
import { resolveCrosshair, drawCrosshair } from "./crosshair";

/**
 * Top Canvas 2D layer (plan §3, layer 3): the crosshair. Pointer moves
 * redraw only this layer; the engine redraws it after every frame with the
 * new layout.
 *
 * @param {HTMLElement} container
 * @param {object} params
 * @param {object} params.timeScale
 * @param {object} params.priceScale
 * @param {object} params.data - DataController: bars, price precision, bar duration
 * @param {object} params.timeAxis - formats the time label
 * @param {{ colors: object, fontFamily: string }} params.theme
 * @param {(text: string, font: string) => number} params.measure - shared with the text layer
 * @param {() => boolean} params.isSnapEnabled - grid snap, see resolveCrosshair
 * @param {(crosshair: object|null, decimals: number) => void} params.onCrosshairChange - after
 *   every draw, with the price precision of the pair
 */
export const createCrosshairOverlay = (
  container,
  { timeScale, priceScale, data, timeAxis, theme, measure, isSnapEnabled, onCrosshairChange },
) => {
  const textLayer = createTextLayer(container);
  let layout = null;
  let pointer = null;
  let crosshair = null;

  const draw = () => {
    textLayer.beginFrame();

    if (!layout) return;

    crosshair = resolveCrosshair({
      point: pointer,
      layout,
      timeScale,
      priceScale,
      store: data.store,
      barDurationMs: data.barDurationMs,
      isSnapEnabled: isSnapEnabled(),
    });
    onCrosshairChange(crosshair, data.priceFormat.decimals);

    if (crosshair) {
      drawCrosshair({
        textLayer,
        measure,
        crosshair,
        layout,
        timeText: timeAxis.formatCrosshairTime(crosshair.time),
        decimals: data.priceFormat.decimals,
        theme,
      });
    }
  };

  const scheduler = createRenderScheduler(draw);

  return {
    /** Layout of the last frame (layoutFrame); null before the first one. */
    get layout() {
      return layout;
    },

    /** Crosshair of the last draw, see resolveCrosshair; null when hidden. */
    get crosshair() {
      return crosshair;
    },

    resize: textLayer.resize,

    /** Draws at once after a frame of the chart. */
    render: (frameLayout) => {
      layout = frameLayout;
      draw();
    },

    /** @param {{ x: number, y: number } | null} point - CSS px of the chart; null hides the crosshair */
    setPointer: (point) => {
      pointer = point;
      scheduler.invalidate();
    },

    destroy: () => {
      scheduler.destroy();
      textLayer.destroy();
    },
  };
};
